import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * API Service for Threat Modeler
 */
class ThreatModelerAPI {
  constructor(keycloak) {
    this.keycloak = keycloak;
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Add auth interceptor
    this.client.interceptors.request.use(
      async (config) => {
        if (this.keycloak && this.keycloak.token) {
          // Refresh token if needed
          try {
            await this.keycloak.updateToken(30);
            config.headers.Authorization = `Bearer ${this.keycloak.token}`;
          } catch (error) {
            console.error('Token refresh failed:', error);
            this.keycloak.logout();
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  /**
   * Submit threat modeling analysis
   */
  async analyzeSystem(formData) {
    try {
      // If formData is FormData object, use multipart/form-data
      const isFormData = formData instanceof FormData;

      const config = isFormData
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : {};

      const response = await this.client.post('/api/analyze', formData, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Submit Client1 specialized threat modeling analysis
   * Generates reports with only MAESTRO, OWASP LLM Top 10, OWASP Agentic (T1-T15), and ISO42001
   */
  async analyzeClient1System(formData) {
    try {
      // If formData is FormData object, use multipart/form-data
      const isFormData = formData instanceof FormData;

      const config = isFormData
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : {};

      const response = await this.client.post('/api/client1/analyze', formData, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get job status and results
   */
  async getJobStatus(jobId) {
    try {
      const response = await this.client.get(`/api/jobs/${jobId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * List all jobs for current user
   * @param {Object} options - Filter options
   * @param {string} options.status - Filter by job status
   * @param {string} options.source - Filter by source (ui, api, github_scanner)
   * @param {string} options.exclude_sources - Comma-separated sources to exclude
   * @param {number} options.limit - Maximum number of jobs to return
   */
  async listJobs(options = {}) {
    try {
      const params = {};
      if (options.status) params.status = options.status;
      if (options.source) params.source = options.source;
      if (options.exclude_sources) params.exclude_sources = options.exclude_sources;
      if (options.limit) params.limit = options.limit;
      if (options.job_type) params.job_type = options.job_type;

      const response = await this.client.get('/api/jobs', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Download file (report, export, visualization)
   */
  async downloadFile(jobId, fileType) {
    try {
      const response = await this.client.get(
        `/api/jobs/${jobId}/download/${fileType}`,
        { responseType: fileType.includes('graph') || fileType.includes('heatmap') ? 'blob' : 'json' }
      );
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete job
   */
  async deleteJob(jobId) {
    try {
      const response = await this.client.delete(`/api/jobs/${jobId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Health check
   */
  async healthCheck() {
    try {
      const response = await this.client.get('/health');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ===== GITHUB ORGANIZATION SCANNING METHODS =====

  /**
   * Create GitHub scan configuration
   */
  async createGitHubScanConfig(configData) {
    try {
      const response = await this.client.post('/api/github/scan-configs', configData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * List GitHub scan configurations
   */
  async listGitHubScanConfigs() {
    try {
      const response = await this.client.get('/api/github/scan-configs');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update GitHub scan configuration
   */
  async updateGitHubScanConfig(configId, configData) {
    try {
      const response = await this.client.put(`/api/github/scan-configs/${configId}`, configData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete GitHub scan configuration
   */
  async deleteGitHubScanConfig(configId) {
    try {
      const response = await this.client.delete(`/api/github/scan-configs/${configId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Trigger manual GitHub organization scan
   */
  async triggerGitHubScan(organization, maxRepos = 50) {
    try {
      const response = await this.client.post(`/api/github/scan/${organization}?max_repos=${maxRepos}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get GitHub scan results for an organization
   */
  async getGitHubScanResults(organization, limit = 10) {
    try {
      const response = await this.client.get(`/api/github/scan-results/${organization}?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get recent GitHub scan job records
   */
  async getGitHubScanJobs(configId = null, limit = 20) {
    try {
      const params = new URLSearchParams();
      if (configId) params.append('config_id', configId);
      params.append('limit', limit);

      const response = await this.client.get(`/api/github/scan-jobs?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get detailed scan results for a specific scan
   */
  async getGitHubScanDetail(scanId) {
    try {
      const response = await this.client.get(`/api/github/scan-detail/${scanId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ===== COMPLIANCE SCANNING METHODS =====

  /**
   * Submit compliance scanning analysis
   */
  async scanCompliance(scanData) {
    try {
      const response = await this.client.post('/api/compliance/scan', scanData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get compliance job status
   */
  async getComplianceStatus(jobId) {
    try {
      const response = await this.client.get(`/api/compliance/jobs/${jobId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get compliance report
   */
  async getComplianceReport(jobId, format = 'markdown') {
    try {
      const response = await this.client.get(`/api/compliance/jobs/${jobId}/report?format=${format}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * List all ISO 42001 controls
   */
  async listISO42001Controls() {
    try {
      const response = await this.client.get('/api/compliance/controls');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * List jobs with filter for compliance
   */
  async listComplianceJobs(status = null) {
    try {
      const params = new URLSearchParams();
      params.append('job_type', 'compliance_scanning');
      if (status) params.append('status', status);

      const response = await this.client.get(`/api/jobs?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ===== USER PROFILE AND API TOKEN METHODS =====

  /**
   * Get current user profile
   */
  async getUserProfile() {
    try {
      const response = await this.client.get('/api/profile');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update user profile
   */
  async updateUserProfile(profileData) {
    try {
      const response = await this.client.put('/api/profile', profileData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create API token
   */
  async createApiToken(tokenData) {
    try {
      const response = await this.client.post('/api/profile/api-tokens', tokenData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * List API tokens for current user
   */
  async getApiTokens() {
    try {
      const response = await this.client.get('/api/profile/api-tokens');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Revoke API token
   */
  async revokeApiToken(tokenId) {
    try {
      const response = await this.client.delete(`/api/profile/api-tokens/${tokenId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * List GitHub organizations for current user
   */
  async getGithubOrganizations() {
    try {
      const response = await this.client.get('/api/profile/github-organizations');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create GitHub organization
   */
  async createGithubOrganization(orgData) {
    try {
      const response = await this.client.post('/api/profile/github-organizations', orgData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update GitHub organization
   */
  async updateGithubOrganization(orgId, orgData) {
    try {
      const response = await this.client.put(`/api/profile/github-organizations/${orgId}`, orgData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete GitHub organization
   */
  async deleteGithubOrganization(orgId) {
    try {
      const response = await this.client.delete(`/api/profile/github-organizations/${orgId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ===== AZURE DEVOPS ORGANIZATION METHODS =====

  /**
   * Get all Azure DevOps organizations for the current user
   */
  async getAzureDevOpsOrganizations() {
    try {
      const response = await this.client.get('/api/profile/azure-devops-organizations');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create a new Azure DevOps organization configuration
   */
  async createAzureDevOpsOrganization(orgData) {
    try {
      const response = await this.client.post('/api/profile/azure-devops-organizations', orgData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update an Azure DevOps organization configuration
   */
  async updateAzureDevOpsOrganization(orgId, orgData) {
    try {
      const response = await this.client.put(`/api/profile/azure-devops-organizations/${orgId}`, orgData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete an Azure DevOps organization
   */
  async deleteAzureDevOpsOrganization(orgId) {
    try {
      const response = await this.client.delete(`/api/profile/azure-devops-organizations/${orgId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Trigger an Azure DevOps organization scan
   */
  async triggerAzureDevOpsScan(organization, maxRepos = 50, maxProjects = 20, scanPipelines = true, scanArm = true, scanBicep = true) {
    try {
      const params = new URLSearchParams();
      params.append('max_repos', maxRepos);
      params.append('max_projects', maxProjects);
      params.append('scan_pipelines', scanPipelines);
      params.append('scan_arm_templates', scanArm);
      params.append('scan_bicep', scanBicep);

      const response = await this.client.post(`/api/azure-devops/scan/${organization}?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get Azure DevOps scan results for an organization
   */
  async getAzureDevOpsScanResults(organization, limit = 10) {
    try {
      const response = await this.client.get(`/api/azure-devops/scan-results/${organization}?limit=${limit}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get detailed results for a specific Azure DevOps scan
   */
  async getAzureDevOpsScanDetail(scanId) {
    try {
      const response = await this.client.get(`/api/azure-devops/scan-detail/${scanId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get recent Azure DevOps scan jobs
   */
  async getAzureDevOpsScanJobs(configId = null, limit = 20) {
    try {
      const params = new URLSearchParams();
      if (configId) params.append('config_id', configId);
      params.append('limit', limit);

      const response = await this.client.get(`/api/azure-devops/scan-jobs?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ===== NOTIFICATION SYSTEM METHODS =====

  /**
   * Set notification preferences for the current user
   */
  async setNotificationPreferences(preferences) {
    try {
      const response = await this.client.post('/api/notifications/preferences', preferences);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get notification preferences for the current user
   */
  async getNotificationPreferences() {
    try {
      const response = await this.client.get('/api/notifications/preferences');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get in-app notifications for the current user
   */
  async getNotifications(limit = 20, unreadOnly = false) {
    try {
      const params = new URLSearchParams();
      params.append('limit', limit);
      if (unreadOnly) params.append('unread_only', true);

      const response = await this.client.get(`/api/notifications?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Mark a notification as read
   */
  async markNotificationRead(notificationId) {
    try {
      const response = await this.client.post(`/api/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Send a test notification
   */
  async sendTestNotification(notificationType, testMessage = null) {
    try {
      const payload = {
        notification_type: notificationType
      };

      if (testMessage) {
        payload.test_message = testMessage;
      }

      const response = await this.client.post('/api/notifications/test', payload);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Configure SMTP settings (admin only)
   */
  async configureSMTP(smtpConfig) {
    try {
      const response = await this.client.post('/api/notifications/smtp-config', smtpConfig);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get notification statistics
   */
  async getNotificationStats() {
    try {
      const response = await this.client.get('/api/notifications/stats');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ===== USER LICENSE METHODS =====

  /**
   * Get current user's license information
   */
  async getUserLicense() {
    try {
      const response = await this.client.get('/api/user/license');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ===== ADMIN TENANT MANAGEMENT METHODS =====

  /**
   * List all tenants (admin only)
   */
  async listTenants(status = null, limit = 100, offset = 0) {
    try {
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      params.append('limit', limit);
      params.append('offset', offset);

      const response = await this.client.get(`/api/admin/tenants?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create a new tenant (admin only)
   */
  async createTenant(tenantData) {
    try {
      const response = await this.client.post('/api/admin/tenants', tenantData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get tenant details (admin only)
   */
  async getTenant(tenantId) {
    try {
      const response = await this.client.get(`/api/admin/tenants/${tenantId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update tenant (admin only)
   */
  async updateTenant(tenantId, tenantData) {
    try {
      const response = await this.client.put(`/api/admin/tenants/${tenantId}`, tenantData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete (suspend) tenant (admin only)
   */
  async deleteTenant(tenantId) {
    try {
      const response = await this.client.delete(`/api/admin/tenants/${tenantId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Assign license to tenant (admin only)
   */
  async assignLicense(tenantId, licenseData) {
    try {
      const response = await this.client.post(`/api/admin/tenants/${tenantId}/licenses`, licenseData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * List licenses for tenant (admin only)
   */
  async listTenantLicenses(tenantId) {
    try {
      const response = await this.client.get(`/api/admin/tenants/${tenantId}/licenses`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Deactivate license (admin only)
   */
  async deactivateLicense(licenseId) {
    try {
      const response = await this.client.delete(`/api/admin/licenses/${licenseId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * List users in a tenant (admin only)
   */
  async listTenantUsers(tenantId) {
    try {
      const response = await this.client.get(`/api/admin/tenants/${tenantId}/users`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add user to tenant (admin only)
   */
  async addUserToTenant(tenantId, userData) {
    try {
      const response = await this.client.post(`/api/admin/tenants/${tenantId}/users`, userData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update user role in tenant (admin only)
   */
  async updateUserRole(tenantId, userId, role) {
    try {
      const response = await this.client.put(`/api/admin/tenants/${tenantId}/users/${userId}`, { role });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Remove user from tenant (admin only)
   */
  async removeUserFromTenant(tenantId, userId) {
    try {
      const response = await this.client.delete(`/api/admin/tenants/${tenantId}/users/${userId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get tenant usage stats (admin only)
   */
  async getTenantUsage(tenantId, months = 6) {
    try {
      const response = await this.client.get(`/api/admin/tenants/${tenantId}/usage?months=${months}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get admin dashboard stats (admin only)
   */
  async getAdminStats() {
    try {
      const response = await this.client.get('/api/admin/stats');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get license defaults (admin only)
   */
  async getLicenseDefaults() {
    try {
      const response = await this.client.get('/api/admin/license-defaults');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ===== ADMIN GOLDEN STANDARDS MANAGEMENT METHODS =====

  /**
   * List golden standards for a tenant (admin only)
   */
  async listTenantGoldenStandards(tenantId, category = null, includeInactive = false) {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (includeInactive) params.append('include_inactive', 'true');

      const response = await this.client.get(`/api/admin/tenants/${tenantId}/golden-standards?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create golden standard for a tenant (admin only)
   */
  async createGoldenStandard(tenantId, standardData) {
    try {
      const response = await this.client.post(`/api/admin/tenants/${tenantId}/golden-standards`, standardData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get a specific golden standard (admin only)
   */
  async getGoldenStandard(tenantId, standardId) {
    try {
      const response = await this.client.get(`/api/admin/tenants/${tenantId}/golden-standards/${standardId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update golden standard (admin only)
   */
  async updateGoldenStandard(tenantId, standardId, standardData) {
    try {
      const response = await this.client.put(`/api/admin/tenants/${tenantId}/golden-standards/${standardId}`, standardData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete golden standard (admin only)
   */
  async deleteGoldenStandard(tenantId, standardId, hardDelete = false) {
    try {
      const params = hardDelete ? '?hard_delete=true' : '';
      const response = await this.client.delete(`/api/admin/tenants/${tenantId}/golden-standards/${standardId}${params}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Initialize default golden standards for a tenant (admin only)
   */
  async initializeDefaultGoldenStandards(tenantId) {
    try {
      const response = await this.client.post(`/api/admin/tenants/${tenantId}/golden-standards/initialize-defaults`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get all available golden standard categories (admin only)
   */
  async getGoldenStandardCategories() {
    try {
      const response = await this.client.get('/api/admin/golden-standards/categories');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get all available golden standard classifications (admin only)
   */
  async getGoldenStandardClassifications() {
    try {
      const response = await this.client.get('/api/admin/golden-standards/classifications');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get all available risk levels (admin only)
   */
  async getGoldenStandardRiskLevels() {
    try {
      const response = await this.client.get('/api/admin/golden-standards/risk-levels');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ===== ORGANIZATION (TENANT-SCOPED) API METHODS =====

  /**
   * Get current user's organization info
   */
  async getOrganizationInfo() {
    try {
      const response = await this.client.get('/api/organization/info');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * List golden standards for current user's organization
   */
  async listOrganizationGoldenStandards(category = null, includeInactive = false) {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (includeInactive) params.append('include_inactive', 'true');

      const response = await this.client.get(`/api/organization/golden-standards?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create golden standard for current user's organization
   */
  async createOrganizationGoldenStandard(standardData) {
    try {
      const response = await this.client.post('/api/organization/golden-standards', standardData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get a specific golden standard from current user's organization
   */
  async getOrganizationGoldenStandard(standardId) {
    try {
      const response = await this.client.get(`/api/organization/golden-standards/${standardId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update golden standard in current user's organization
   */
  async updateOrganizationGoldenStandard(standardId, standardData) {
    try {
      const response = await this.client.put(`/api/organization/golden-standards/${standardId}`, standardData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete golden standard from current user's organization
   */
  async deleteOrganizationGoldenStandard(standardId, hardDelete = false) {
    try {
      const params = hardDelete ? '?hard_delete=true' : '';
      const response = await this.client.delete(`/api/organization/golden-standards/${standardId}${params}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Initialize default golden standards for current user's organization
   */
  async initializeOrganizationDefaultGoldenStandards() {
    try {
      const response = await this.client.post('/api/organization/golden-standards/initialize-defaults');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get golden standard categories
   */
  async getOrganizationGoldenStandardCategories() {
    try {
      const response = await this.client.get('/api/organization/golden-standards-metadata/categories');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Add a user to the current user's organization
   */
  async addUserToOrganization(userData) {
    try {
      const response = await this.client.post('/api/organization/users', userData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update a user's role in the organization
   */
  async updateOrganizationUserRole(userId, role) {
    try {
      const response = await this.client.put(`/api/organization/users/${userId}/role`, { role });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Remove a user from the organization
   */
  async removeUserFromOrganization(userId) {
    try {
      const response = await this.client.delete(`/api/organization/users/${userId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get organization settings (API keys, preferences)
   */
  async getOrganizationSettings() {
    try {
      const response = await this.client.get('/api/organization/settings');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update organization settings (API keys, preferences)
   */
  async updateOrganizationSettings(settings) {
    try {
      const response = await this.client.put('/api/organization/settings', settings);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete organization API key for a specific provider
   */
  async deleteOrganizationApiKey(provider) {
    try {
      const response = await this.client.delete(`/api/organization/settings/api-key/${provider}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error
      const message = error.response.data?.error || error.response.data?.message || error.response.data?.detail || 'Server error';
      return new Error(message);
    } else if (error.request) {
      // Request made but no response
      return new Error('No response from server. Please check your connection.');
    } else {
      // Something else happened
      return new Error(error.message || 'An unexpected error occurred');
    }
  }
}

export default ThreatModelerAPI;
